import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Bell, AlertTriangle, Zap, CheckCircle2, Rocket, GitPullRequest, MessageSquare, Clock } from 'lucide-react';
export function NotificationPreferences() {
  const [prefs, setPrefs] = useState({
    sources: {
      jira: true,
      github: true,
      confluence: true,
      slack: true,
      agent: true
    },
    types: {
      blocker: true,
      sprint_alert: true,
      issue_update: false,
      deploy: true,
      pr: true,
      agent_interaction: true
    },
    quietHours: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00'
  });
  const toggleSource = (key: keyof typeof prefs.sources) => {
    setPrefs(p => ({
      ...p,
      sources: {
        ...p.sources,
        [key]: !p.sources[key]
      }
    }));
  };
  const toggleType = (key: keyof typeof prefs.types) => {
    setPrefs(p => ({
      ...p,
      types: {
        ...p.types,
        [key]: !p.types[key]
      }
    }));
  };
  const toggleQuietHours = () => {
    setPrefs(p => ({
      ...p,
      quietHours: !p.quietHours
    }));
  };
  return <Card className="bg-slate-900 border-slate-800" data-id="element-5356">
      <CardHeader data-id="element-5357">
        <CardTitle className="flex items-center gap-2" data-id="element-5358">
          <Bell size={20} className="text-indigo-400" data-id="element-5359" /> Preferencias de
          Notificaciones
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6" data-id="element-5360">
        {/* Sources */}
        <div data-id="element-5361">
          <h3 className="text-sm font-medium text-slate-300 mb-3" data-id="element-5362">
            Fuentes Activas
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-5363">
            {[{
            id: 'jira',
            label: 'Jira Software'
          }, {
            id: 'github',
            label: 'GitHub'
          }, {
            id: 'confluence',
            label: 'Confluence'
          }, {
            id: 'slack',
            label: 'Slack'
          }, {
            id: 'agent',
            label: 'Agente IA'
          }].map(source => <label key={source.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-800 bg-slate-950/50 cursor-pointer hover:bg-slate-900 transition-colors" data-id="element-5364">
                <input type="checkbox" checked={prefs.sources[source.id as keyof typeof prefs.sources]} onChange={() => toggleSource(source.id as keyof typeof prefs.sources)} className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-slate-950" data-id="element-5365" />

                <span className="text-sm text-slate-300" data-id="element-5366">{source.label}</span>
              </label>)}
          </div>
        </div>

        {/* Types */}
        <div data-id="element-5367">
          <h3 className="text-sm font-medium text-slate-300 mb-3" data-id="element-5368">
            Tipos de Alertas
          </h3>
          <div className="space-y-2" data-id="element-5369">
            {[{
            id: 'blocker',
            label: 'Bloqueadores',
            desc: 'Alertas cuando un issue se bloquea',
            icon: AlertTriangle,
            color: 'text-red-400'
          }, {
            id: 'sprint_alert',
            label: 'Alertas de Sprint',
            desc: 'Hitos y riesgos del sprint actual',
            icon: Zap,
            color: 'text-amber-400'
          }, {
            id: 'issue_update',
            label: 'Actualizaciones de Issues',
            desc: 'Cambios de estado y asignación',
            icon: CheckCircle2,
            color: 'text-emerald-400'
          }, {
            id: 'deploy',
            label: 'Deploys',
            desc: 'Despliegues a staging y producción',
            icon: Rocket,
            color: 'text-cyan-400'
          }, {
            id: 'pr',
            label: 'Pull Requests',
            desc: 'Nuevos PRs y reviews solicitadas',
            icon: GitPullRequest,
            color: 'text-violet-400'
          }, {
            id: 'agent_interaction',
            label: 'Interacciones del Agente',
            desc: 'Cuando el agente necesita tu confirmación',
            icon: MessageSquare,
            color: 'text-indigo-400'
          }].map(type => <div key={type.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-800 bg-slate-950/50" data-id="element-5370">
                <div className="flex items-center gap-3" data-id="element-5371">
                  <div className="p-2 bg-slate-900 rounded-lg" data-id="element-5372">
                    <type.icon size={16} className={type.color} data-id="element-5373" />
                  </div>
                  <div data-id="element-5374">
                    <p className="text-sm font-medium text-slate-200" data-id="element-5375">
                      {type.label}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-5376">{type.desc}</p>
                  </div>
                </div>
                <button onClick={() => toggleType(type.id as keyof typeof prefs.types)} className={`relative w-11 h-6 rounded-full transition-colors ${prefs.types[type.id as keyof typeof prefs.types] ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-5377">
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.types[type.id as keyof typeof prefs.types] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5378" />
                </button>
              </div>)}
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="p-4 rounded-xl border border-slate-800 bg-slate-950/50" data-id="element-5379">
          <div className="flex items-center justify-between mb-4" data-id="element-5380">
            <div className="flex items-center gap-3" data-id="element-5381">
              <div className="p-2 bg-slate-900 rounded-lg" data-id="element-5382">
                <Clock size={16} className="text-slate-400" data-id="element-5383" />
              </div>
              <div data-id="element-5384">
                <p className="text-sm font-medium text-slate-200" data-id="element-5385">
                  Horario Silencioso
                </p>
                <p className="text-xs text-slate-500" data-id="element-5386">
                  Pausar notificaciones no críticas
                </p>
              </div>
            </div>
            <button onClick={toggleQuietHours} className={`relative w-11 h-6 rounded-full transition-colors ${prefs.quietHours ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-5387">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.quietHours ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5388" />
            </button>
          </div>

          {prefs.quietHours && <div className="flex items-center gap-4 pt-4 border-t border-slate-800" data-id="element-5389">
              <div className="flex-1" data-id="element-5390">
                <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-5391">
                  Desde
                </label>
                <input type="time" value={prefs.quietHoursStart} onChange={e => setPrefs(p => ({
              ...p,
              quietHoursStart: e.target.value
            }))} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-5392" />
              </div>
              <div className="flex-1" data-id="element-5393">
                <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-5394">
                  Hasta
                </label>
                <input type="time" value={prefs.quietHoursEnd} onChange={e => setPrefs(p => ({
              ...p,
              quietHoursEnd: e.target.value
            }))} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-5395" />
              </div>
            </div>}
        </div>
      </CardContent>
    </Card>;
}